import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ExtendedLead } from '@/components/ClientDetails/types';
import type { LeadContato } from '@/types/leadContatos';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from '@/hooks/use-toast';
import { CalendarClock, Pencil, Phone, Plus, Trash2 } from 'lucide-react';

interface ContatosProps {
  leadData: ExtendedLead;
  onUpdate: () => void;
  readOnly?: boolean;
}

function formatDateBR(iso: string | undefined | null, withTime = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' };
  return d.toLocaleString('pt-BR', opts);
}

function isoDateToInputDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inputDateToIsoEndOfDay(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toISOString();
}

function isoToDatetimeLocalValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Garante ano com no máximo 4 dígitos (alguns browsers permitem 5+ no datetime-local). */
function clampDatetimeLocalYearToFourDigits(value: string): string {
  if (!value) return '';
  const t = value.indexOf('T');
  if (t <= 0) return value;
  const datePart = value.slice(0, t);
  const timePart = value.slice(t);
  const hy = datePart.indexOf('-');
  if (hy <= 0) return value;
  const yearStr = datePart.slice(0, hy);
  const md = datePart.slice(hy);
  if (yearStr.length <= 4) return value;
  return `${yearStr.slice(0, 4)}${md}${timePart}`;
}

/** Data civil local (YYYY-MM-DD) a partir de hoje, para `max` em input type="date". */
function todayYmdLocal(): string {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const day = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** True se a data do contato (só dia) for depois de hoje no fuso local. */
function isContactDateAfterTodayLocal(ymd: string): boolean {
  if (!ymd) return false;
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return false;
  const [y, mo, d] = parts;
  const selected = new Date(y, mo - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selected.setHours(0, 0, 0, 0);
  return selected.getTime() > today.getTime();
}

/** Próximo contato não pode ser em instante já passado (inclui agora). */
function isNextContactInPastOrNow(localDatetime: string): boolean {
  if (!localDatetime) return false;
  const d = new Date(localDatetime);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() <= Date.now();
}

const PROXIMO_HORA_MIN_STRICT = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Completa o valor de `datetime-local`: só data ou horário vazio/incompleto → 18:00;
 * hora sem minutos → :00 na hora informada.
 */
function normalizeProximoDatetimeLocalDefault1800(value: string): string {
  const v = value.trim();
  if (!v) return '';
  const t = v.indexOf('T');
  if (t === -1) {
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T18:00` : v;
  }
  const datePart = v.slice(0, t);
  const timePart = v.slice(t + 1).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return v;
  if (timePart === '') return `${datePart}T18:00`;
  if (PROXIMO_HORA_MIN_STRICT.test(timePart)) return `${datePart}T${timePart}`;

  const m = timePart.match(/^(\d{1,2})(?::(\d{1,2})?)?$/);
  if (!m) return `${datePart}T18:00`;
  const hh = parseInt(m[1], 10);
  const mmRaw = m[2];
  const mm = mmRaw === undefined || mmRaw === '' ? 0 : parseInt(mmRaw, 10);
  if (Number.isNaN(hh) || hh < 0 || hh > 23 || Number.isNaN(mm) || mm < 0 || mm > 59) {
    return `${datePart}T18:00`;
  }
  const padded = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  return `${datePart}T${padded}`;
}

function finalizeProximoLocalInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return clampDatetimeLocalYearToFourDigits(normalizeProximoDatetimeLocalDefault1800(trimmed));
}

/** Monta o valor a partir de `date` + `time` opcionais (sem `datetime-local`, que zera `value` se o horário ficar vazio). */
function buildProximoLocalFromParts(ymd: string, hm: string): string {
  const d = ymd.trim();
  if (!d) return '';
  const t = hm.trim();
  const combined = t ? `${d}T${t}` : `${d}T18:00`;
  return finalizeProximoLocalInput(combined);
}

const emptyForm = () => ({
  dataContatoYmd: isoDateToInputDate(new Date().toISOString()),
  resumo: '',
  proximoContatoYmd: '',
  proximoContatoHm: '',
});

export default function Contatos({ leadData, onUpdate, readOnly = false }: ContatosProps) {
  const [items, setItems] = useState<LeadContato[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LeadContato | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LeadContato | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ dataContato?: string; proximoContato?: string }>({});

  const load = useCallback(async () => {
    if (!leadData.id) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/leads/${leadData.id}/contatos`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string; code?: string }));
        throw new Error(
          typeof body.error === 'string' && body.error.length > 0
            ? body.error
            : `Falha ao carregar contatos (${res.status})`
        );
      }
      const data: LeadContato[] = await res.json();
      setItems(data);
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Não foi possível carregar os contatos.';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [leadData.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (c: LeadContato) => {
    setEditing(c);
    setFieldErrors({});
    let proximoContatoYmd = '';
    let proximoContatoHm = '';
    if (c.proximoContatoEm) {
      const local = isoToDatetimeLocalValue(c.proximoContatoEm);
      const ti = local.indexOf('T');
      if (ti > 0) {
        proximoContatoYmd = local.slice(0, ti);
        proximoContatoHm = local.slice(ti + 1);
      }
    }
    setForm({
      dataContatoYmd: isoDateToInputDate(c.dataContato),
      resumo: c.resumo,
      proximoContatoYmd,
      proximoContatoHm,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!leadData.id) return;
    const resumo = form.resumo.trim();
    if (!resumo) {
      toast({ title: 'Atenção', description: 'Preencha as informações do contato.', variant: 'destructive' });
      return;
    }
    if (!form.dataContatoYmd) {
      toast({ title: 'Atenção', description: 'Informe a data do contato.', variant: 'destructive' });
      return;
    }

    const nextErrors: { dataContato?: string; proximoContato?: string } = {};
    if (isContactDateAfterTodayLocal(form.dataContatoYmd)) {
      nextErrors.dataContato =
        'Não é possível registrar um contato em uma data que ainda não chegou. Use a data em que o contato ocorreu.';
    }
    const proximoFinal = buildProximoLocalFromParts(form.proximoContatoYmd, form.proximoContatoHm);

    if (proximoFinal && isNextContactInPastOrNow(proximoFinal)) {
      nextErrors.proximoContato =
        'Não é possível agendar o próximo contato em uma data e hora que já passaram.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      const body = {
        dataContato: inputDateToIsoEndOfDay(form.dataContatoYmd),
        resumo,
        proximoContatoEm: proximoFinal ? datetimeLocalToIso(proximoFinal) : null,
      };
      const url = editing
        ? `/api/leads/${leadData.id}/contatos/${editing.id}`
        : `/api/leads/${leadData.id}/contatos`;
      const res = await fetchWithAuth(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(
          typeof err.error === 'string' && err.error.length > 0 ? err.error : 'Erro ao salvar'
        );
      }
      toast({ title: editing ? 'Contato atualizado' : 'Contato registrado' });
      setDialogOpen(false);
      await load();
      onUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao salvar';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !leadData.id) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `/api/leads/${leadData.id}/contatos/${deleteTarget.id}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(
          typeof err.error === 'string' && err.error.length > 0 ? err.error : 'Erro ao excluir'
        );
      }
      toast({ title: 'Contato removido' });
      setDeleteTarget(null);
      await load();
      onUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao excluir';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Phone className="h-5 w-5" />
              Contatos
            </CardTitle>
            <CardDescription>
              Histórico de interações com o lead: o que foi tratado, quando ocorreu e quando retornar o contato.
            </CardDescription>
          </div>
          {!readOnly && (
            <Button type="button" onClick={openNew} className="shrink-0">
              <Plus className="h-4 w-4 mr-2" />
              Novo contato
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum contato registrado ainda.
              {!readOnly && ' Clique em "Novo contato" para adicionar.'}
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((c) => (
                <li
                  key={c.id}
                  className="rounded-lg border border-border bg-muted/20 p-4 space-y-2"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1 min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Contato em {formatDateBR(c.dataContato, false)}
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap break-words">{c.resumo}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <CalendarClock className="h-3.5 w-3.5" />
                          Próximo contato:{' '}
                          {c.proximoContatoEm ? formatDateBR(c.proximoContatoEm, true) : 'Não agendado'}
                        </span>
                      </div>
                    </div>
                    {!readOnly && (
                      <div className="flex gap-1 shrink-0">
                        <Button type="button" variant="outline" size="icon" onClick={() => openEdit(c)} aria-label="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setDeleteTarget(c)}
                          aria-label="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setFieldErrors({});
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar contato' : 'Novo contato'}</DialogTitle>
            <DialogDescription>
              Registre o que foi conversado, a data do contato e, se quiser, quando deve ser o próximo retorno.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="contato-data">Data do contato</Label>
              <Input
                id="contato-data"
                type="date"
                max={todayYmdLocal()}
                aria-invalid={!!fieldErrors.dataContato}
                className={fieldErrors.dataContato ? 'border-destructive' : undefined}
                value={form.dataContatoYmd}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, dataContatoYmd: v }));
                  setFieldErrors((er) => ({ ...er, dataContato: undefined }));
                }}
              />
              {fieldErrors.dataContato ? (
                <p className="text-sm text-destructive" role="alert">
                  {fieldErrors.dataContato}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contato-resumo">Informações importantes</Label>
              <Textarea
                id="contato-resumo"
                rows={5}
                placeholder="Resumo do que foi tratado, acordos, objeções, próximos passos…"
                value={form.resumo}
                onChange={(e) => setForm((f) => ({ ...f, resumo: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Próximo contato (opcional)</Label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Label htmlFor="contato-proximo-data" className="text-xs font-normal text-muted-foreground">
                    Data
                  </Label>
                  <Input
                    id="contato-proximo-data"
                    type="date"
                    min={todayYmdLocal()}
                    max="2099-12-31"
                    aria-invalid={!!fieldErrors.proximoContato}
                    className={fieldErrors.proximoContato ? 'border-destructive' : undefined}
                    value={form.proximoContatoYmd}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, proximoContatoYmd: e.target.value }));
                      setFieldErrors((er) => ({ ...er, proximoContato: undefined }));
                    }}
                  />
                </div>
                <div className="w-full shrink-0 space-y-1.5 sm:w-40">
                  <Label htmlFor="contato-proximo-hora" className="text-xs font-normal text-muted-foreground">
                    Horário (opcional)
                  </Label>
                  <Input
                    id="contato-proximo-hora"
                    type="time"
                    step={60}
                    aria-invalid={!!fieldErrors.proximoContato}
                    className={fieldErrors.proximoContato ? 'border-destructive' : undefined}
                    value={form.proximoContatoHm}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, proximoContatoHm: e.target.value }));
                      setFieldErrors((er) => ({ ...er, proximoContato: undefined }));
                    }}
                  />
                </div>
              </div>
              {fieldErrors.proximoContato ? (
                <p className="text-sm text-destructive" role="alert">
                  {fieldErrors.proximoContato}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Se preencher a data e deixar o horário em branco, o sistema usa 18:00 no dia
                  escolhido.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este contato?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O registro será removido do histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()} disabled={saving}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
